import React, { FC, useContext, useEffect, useState } from 'react';
import {
  Box,
  Text,
  useMediaQuery,
  belowBreakpoint,
  Theme,
  useToastsDispatch,
  ToastAction,
  ToastType,
} from '@bechtel/gds';
import { PlanningDateWindowStyled, LockIconStyled } from './styled';
import { WorkPackage, Scalars } from '@bechtel/types';
import { ThemeContext } from 'styled-components';
import { parseISO, isWithinInterval } from 'date-fns';
import { map } from 'lodash';
import { TOAST_ID_WORK_PACKAGE } from '@bechtel/client-common';
import { t, Trans } from '@lingui/macro';

interface PlanningDateWindowProps {
  workPackageOutsideWLACount: Scalars['Int'];
  selectedWorkPackageCount: Scalars['Int'];
}

export function workPackagesOutsideWLA(
  selectedWorkpackageList: WorkPackage[],
  weekStartDate: Scalars['Date'],
  weekEndDate: Scalars['Date']
): WorkPackage[] {
  const workPackagesOutsideWLA: WorkPackage[] = [];
  const parsedWeekStartDate = weekStartDate && parseISO(weekStartDate);
  const parsedWeekEndDate = weekEndDate && parseISO(weekEndDate);
  map(selectedWorkpackageList, (wpItem: WorkPackage) => {
    const parsedWpStartDate = wpItem && wpItem.startDate && parseISO(wpItem.startDate);
    const parsedWpEndDate = wpItem && wpItem.endDate && parseISO(wpItem.endDate);
    const isPlannable =
      parsedWpStartDate &&
      parsedWpEndDate &&
      parsedWeekStartDate &&
      parsedWeekEndDate &&
      parsedWpStartDate < parsedWpEndDate &&
      (isWithinInterval(parsedWpStartDate, { start: parsedWeekStartDate, end: parsedWeekEndDate }) ||
        isWithinInterval(parsedWpEndDate, { start: parsedWeekStartDate, end: parsedWeekEndDate }) ||
        isWithinInterval(parsedWeekStartDate, { start: parsedWpStartDate, end: parsedWpEndDate }) ||
        isWithinInterval(parsedWeekEndDate, { start: parsedWpStartDate, end: parsedWpEndDate }));
    if (!isPlannable) workPackagesOutsideWLA.push(wpItem);
  });
  return workPackagesOutsideWLA;
}

const PlanningDateWindow: FC<PlanningDateWindowProps> = ({ workPackageOutsideWLACount, selectedWorkPackageCount }) => {
  const dispatchToast = useToastsDispatch();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isDesktop = useMediaQuery(belowBreakpoint(breakpoints.desktop));
  const [showWorkPackageToast, setShowWorkPackageToast] = useState<boolean>(false);
  let toastPartialMessage = '';
  if (workPackageOutsideWLACount === 1 && selectedWorkPackageCount === 1) {
    toastPartialMessage = t`This work package`;
  } else if (workPackageOutsideWLACount === 1 && selectedWorkPackageCount > 1) {
    toastPartialMessage = t`One of the selected work packages`;
  } else if (
    workPackageOutsideWLACount > 1 &&
    selectedWorkPackageCount > 1 &&
    workPackageOutsideWLACount === selectedWorkPackageCount
  ) {
    toastPartialMessage = t`All selected work packages`;
  } else if (
    workPackageOutsideWLACount > 1 &&
    selectedWorkPackageCount > 1 &&
    workPackageOutsideWLACount !== selectedWorkPackageCount
  ) {
    toastPartialMessage = t`Some of the selected work packages`;
  }
  const WorkPackageOutsideWLAToast = () => {
    return (
      <Box p={{ sm: isDesktop ? '400' : '550' }} flexDirection={{ sm: 'column' }}>
        <Box mb={{ sm: '450' }}>
          <Text family="graphik" weight="600" size={isDesktop ? '400' : '500'}>
            {toastPartialMessage} <Trans>schedule is outside 3WLA.</Trans>
          </Text>
        </Box>
        <Box mb={{ sm: '450' }}>
          <Text family="graphik" weight="600" size={isDesktop ? '400' : '500'}>
            <Trans>Reschedule work package to finish planning</Trans>
          </Text>
        </Box>
        <Text family="graphik" weight="600" size={isDesktop ? '400' : '500'}>
          <Trans>unclaimed milestones.</Trans>
        </Text>
      </Box>
    );
  };
  const workPackageToast: ToastAction = {
    type: 'ADD_TOAST',
    payload: {
      id: TOAST_ID_WORK_PACKAGE,
      toastType: ToastType.INFO,
      content: WorkPackageOutsideWLAToast,
      alertAction: {
        label: 'GOT IT',
        onClickHandler: () => {},
      },
    },
  };
  useEffect(() => {
    if (workPackageOutsideWLACount > 0) {
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: TOAST_ID_WORK_PACKAGE } });
      setShowWorkPackageToast(true);
    }
  }, [workPackageOutsideWLACount, dispatchToast]);

  useEffect(() => {
    if (showWorkPackageToast) {
      dispatchToast(workPackageToast);
      setShowWorkPackageToast(!showWorkPackageToast);
    }
  }, [showWorkPackageToast, workPackageToast, dispatchToast]);

  return (
    <PlanningDateWindowStyled px={{ sm: isDesktop ? '200' : '450' }} ml={{ sm: isDesktop ? '200' : '500' }}>
      <LockIconStyled />
      <Text size="200" weight="600" color="white">
        <Trans>Date outside 3WLA</Trans>
        {workPackageOutsideWLACount > 1 ? ` (${workPackageOutsideWLACount})` : ''}
      </Text>
    </PlanningDateWindowStyled>
  );
};

export default PlanningDateWindow;
